import { NodeCell, ElementPropertyConfig, FarrisPropConfigWithData } from "@edp-pmf/grapheditor";
import { MxGraphNS, mxResources } from "@edp-pmf/mxgraph-ts";
import { CallActivitySubProcess, Component, BackWardRule, ActualParameter, VariableSetting, SemanticExpression, CounterSignRule, Condition } from "@gsp-wf/wf-process-model";
import { PropertyHelper } from "./PropertyHelper";
import { BpmnModelHelper, Expression, FlowNodeState, MultiInstanceLoopCharacteristics } from "@edp-pmf/bpmn-model";
import { WfConstants } from "../WfConstants";
import { CallActivitySubProcessPropertyKeys } from "./property-keys/CallActivitySubProcessPropertyKeys";

/**
 * 子流程活动节点
 */
export class CallActivitySubProcessCell extends NodeCell {
    clrTypeId: string = CallActivitySubProcessCell.CLR_TYPE_ID;
    name: string = "子流程";
    nameLanguage: { [lang: string]: string; } = {
      "zh-CHS": "子流程",
      "zh-CHT": "子流程",
      "en": "Sub-Process",
  };
    static CLR_TYPE_ID: string = CallActivitySubProcess.Clr_Type_ID;
    flowElement: CallActivitySubProcess;

    imgUrls = {
        [FlowNodeState.Default]: WfConstants.IMAGE_PATH + "/sub.png",
        [FlowNodeState.Selected]: WfConstants.IMAGE_PATH + "/sub_select.svg",
    };

    constructor(value?: any, geometry?: MxGraphNS.mxGeometry, style?: string) {
        super(value, geometry, style);
    }

    createFlowElement(flowElt?: CallActivitySubProcess) {
        const flowNode = super.createFlowElement(flowElt) as CallActivitySubProcess;
        flowNode.Id = "CallActivitySubProcess" + BpmnModelHelper.GenerateElementId();

        return flowNode;
    }

    getFarrisPropConfig(): FarrisPropConfigWithData {
        let act = this.flowElement;
        // 组织属性框
        let basicProperty: ElementPropertyConfig = new ElementPropertyConfig();
        let activityProperty: ElementPropertyConfig = new ElementPropertyConfig();
        let propertyData: any = {};

        // 基本属性（名称）
        basicProperty.categoryId = 'basicProperty';
        basicProperty.categoryName = mxResources.get('basicInformation');
        basicProperty.tabId = 'basic';
        basicProperty.tabName = mxResources.get('basicAttributes')
        basicProperty.properties = [
            { propertyID: CallActivitySubProcessPropertyKeys.Name, propertyName: mxResources.get('name'), propertyType: 'string', visible: !this.editorUi.multiLangEnabled },
            { propertyID: CallActivitySubProcessPropertyKeys.NameLanguage, propertyName: mxResources.get('nameLanguage'), propertyType: 'multiLanguage', visible: this.editorUi.multiLangEnabled },
            { propertyID: CallActivitySubProcessPropertyKeys.Id, propertyName: mxResources.get('id'), propertyType: 'string', readonly: true }
        ];

        // 活动选项（表单定义+活动构件）
        activityProperty.categoryId = 'activityProperty';
        activityProperty.categoryName = mxResources.get('activityProperty');
        activityProperty.tabId = 'basic';
        activityProperty.tabName = mxResources.get('basicAttributes')
        activityProperty.properties = [
            { propertyID: CallActivitySubProcessPropertyKeys.callActivitySubProcess, propertyName: mxResources.get('callActivitySubProcess'), propertyType: 'modal' },
            { propertyID: CallActivitySubProcessPropertyKeys.variableSettings, propertyName: mxResources.get('variableSettings'), propertyType: 'modal' },
            { propertyID: CallActivitySubProcessPropertyKeys.BackWardRule, propertyName: mxResources.get('backWardRule'), propertyType: 'modal' },
            { propertyID: CallActivitySubProcessPropertyKeys.MultiInstanceLoopCharacteristics, propertyName: mxResources.get('multiInstanceLoopCharacteristics'), propertyType: 'modal' }
        ];

        propertyData[CallActivitySubProcessPropertyKeys.Id] = act.Id;
        propertyData[CallActivitySubProcessPropertyKeys.ClrTypeID] = act.ClrTypeID;
        propertyData[CallActivitySubProcessPropertyKeys.Name] = act.Name;
        propertyData[CallActivitySubProcessPropertyKeys.NameLanguage] = act.NameLanguage || {
            "zh-CHS": act.Name,
        };
        propertyData[CallActivitySubProcessPropertyKeys.callActivitySubProcess] = {
            calledBizDefKey: act.calledBizDefKey,
            calledElement: act.CalledElement,
            inheritVariables: act.inheritVariables,
            inheritBizDataId: act.inheritBizDataId,
            bizDataId: act.bizDataId,
            aifProcessCode: act.aifProcessCode,
            useAutoCreationData: act.useAutoCreationData,
        };
        propertyData[CallActivitySubProcessPropertyKeys.BackWardRule] = act.backWardRule;
        propertyData[CallActivitySubProcessPropertyKeys.variableSettings] = PropertyHelper.getVariableSettings(act.Model, act.variableSettings);
        propertyData[CallActivitySubProcessPropertyKeys.MultiInstanceLoopCharacteristics] = act.LoopCharacteristics;
        
        return {
            propertyConfig: [basicProperty, activityProperty],
            propertyData: propertyData
        }
    }

    updateProps(obj: any) {
        const subProcess = this.flowElement;
        const propertyId: string = obj.propertyID;
        switch (propertyId) {
            case CallActivitySubProcessPropertyKeys.callActivitySubProcess:
                this.updateSubProcessConfig(subProcess, obj);
                break;
            case CallActivitySubProcessPropertyKeys.BackWardRule:
                this.updateBackWardRule(subProcess, obj);
                break;
            case CallActivitySubProcessPropertyKeys.variableSettings:
                this.updateVariableSettings(subProcess, obj);
                break;
            case CallActivitySubProcessPropertyKeys.MultiInstanceLoopCharacteristics:
                this.updateMultiInstanceLoopCharacteristic(subProcess, obj);
                break;
            default:
                super.updateProps(obj);
                break;
        }
    }

    updateSubProcessConfig(subProcess: CallActivitySubProcess, obj: any) {
        const config: any = obj.propertyValue;
        if (subProcess) {
            subProcess.calledBizDefKey = config.calledBizDefKey;
            subProcess.CalledElement = config.calledElement;
            subProcess.inheritBizDataId = config.inheritBizDataId;
            subProcess.inheritVariables = config.inheritVariables;
            subProcess.bizDataId = config.bizDataId;
            subProcess.aifProcessCode = config.aifProcessCode;
            subProcess.useAutoCreationData = config.useAutoCreationData;
        }
    }

    updateBackWardRule(subProcess: CallActivitySubProcess, obj: any) {
        const data = obj.propertyValue;
        let backWardRule = new BackWardRule(subProcess.Model);
        backWardRule.TargetRef = data.TargetRef;
        backWardRule.SelectTargetAble = data.SelectTargetAble;
        backWardRule.WaitReturn = data.WaitReturn;
        backWardRule.ResubmitEffect = data.ResubmitEffect;
        backWardRule.DynamicBackWardRuleAble = data.DynamicBackWardRuleAble;
        backWardRule.SelectTargetList = data.SelectTargetList;

        subProcess.backWardRule = backWardRule;
    }

    updateVariableSettings(subProcess: CallActivitySubProcess, obj: any) {
        const bpmnModel = this.flowElement.Model;
        const extendElements = bpmnModel.DefaultProcess.GetExtensionElements();
        if (subProcess.variableSettings && subProcess.variableSettings.length > 0) {
            for (const v of subProcess.variableSettings) {
                if (v.tag === 'component') {
                    const i = extendElements.findIndex(e => (e instanceof Component && e.Id === v.value));
                    extendElements.splice(i, 1);
                }
            }
        }
        subProcess.variableSettings = new Array();

        const variables = obj.propertyValue as any[];
        if (variables && variables.length > 0) {
            for (const v of variables) {
                const variableSetting = new VariableSetting(bpmnModel);
                // variableSetting.variableId = v.variableId;
                variableSetting.variableCode = v.variableCode;
                variableSetting.variableName = v.variableName;
                variableSetting.tag = v.tag;
                variableSetting.executionTime = v.executionTime;
                variableSetting.value = v.value;
                variableSetting.valueText = v.valueText;

                subProcess.variableSettings.push(variableSetting);
                if (variableSetting.tag === 'component') {
                    const c = v.component;
                    if (c) {
                        let cpt = new Component(bpmnModel);
                        cpt.MetadataId = c.MetadataId;
                        cpt.MethodCode = c.MethodCode;
                        cpt.Id = c.Id;
                        cpt.Name = c.Name;
                        cpt.ActualParameters = new Array();
                        if (c.ActualParameters && c.ActualParameters.length > 0) {
                            for (let a of c.ActualParameters) {
                                let parameter = new ActualParameter(bpmnModel, a.Name, a.Value);
                                parameter.Tag = a.Tag;
                                cpt.ActualParameters.push(parameter);
                            }
                        }

                        extendElements.push(cpt);
                    }
                }
            }
        }
    }

    updateMultiInstanceLoopCharacteristic(subProcess: CallActivitySubProcess, obj: any) {
        const data = obj.propertyValue;
        let loopCharacteristics = new MultiInstanceLoopCharacteristics(subProcess.Model);
        loopCharacteristics.enable = data.enable;
        loopCharacteristics.sequential = data.sequential;
        loopCharacteristics.loopCardinality = data.loopCardinality;
        loopCharacteristics.completionCondition = data.completionCondition;
        loopCharacteristics.completionConditionRules = data.completionConditionRules;

        subProcess.LoopCharacteristics = loopCharacteristics;
        if(obj.propertyValue) {
            const data = obj.propertyValue;
            let loopCharacteristics = new MultiInstanceLoopCharacteristics(subProcess.Model);
            loopCharacteristics.enable = data.enable;
            loopCharacteristics.sequential = data.sequential;
            loopCharacteristics.loopCardinality = new Expression(subProcess.Model);
            if(data.loopCardinality) {
                let variableExpression = new SemanticExpression(subProcess.Model);
                variableExpression.Value = data.loopCardinality;
                loopCharacteristics.loopCardinality.MexExpression = variableExpression;
            }
            loopCharacteristics.completionCondition = data.completionCondition;
            let rules = data.completionConditionRules as any[];
            loopCharacteristics.completionConditionRules = null;
            if(rules && rules.length > 0) {
                loopCharacteristics.completionConditionRules = new Array<CounterSignRule>();
                for(let rule of rules) {
                    let counterSignRule = new CounterSignRule(subProcess.Model);
                    let condition = new Condition(subProcess.Model);
                    condition.Value = rule.CompletionCondition.Value;
                    counterSignRule.ConclusionValue = rule.ConclusionValue;
                    counterSignRule.CalculateType = rule.CalculateType;
                    counterSignRule.ConditionValue = rule.ConditionValue;
                    counterSignRule.WaitAllVotes = rule.WaitAllVotes;
                    counterSignRule.CompletionCondition = condition;
                    loopCharacteristics.completionConditionRules.push(counterSignRule);
                }
            }
            subProcess.LoopCharacteristics = loopCharacteristics;
        }
    }
}
